/**
 * Org Invite Helpers — Phase 2B
 *
 * Token-based invite flow for Club / School Mode.
 * Invite links are shared manually (copy/paste). No email sending in V1.
 *
 * Token lifecycle: pending → accepted | revoked | expired
 *
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md
 * - Uses `organizations` + `org_invites` tables only.
 * - Idempotent: duplicate calls are safe at every step.
 * - claim_pending memberships are separate from invite flow.
 *
 * Invite link format:
 *   In-app deep link : duel://org/invite/<token>
 *   Web / TestFlight  : https://douevenlift.com/join/<token>
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import type { OrgInvite, OrgInviteRole } from '@/types/database';

export type { OrgInvite, OrgInviteRole };

const log = createLogger('ORG_INVITE');

/** Base URL for shareable invite links */
export const INVITE_BASE_URL = 'https://douevenlift.com/join';

/** Construct a shareable invite link from a token */
export function buildInviteLink(token: string): string {
  return `${INVITE_BASE_URL}/${token}`;
}

// ─── createOrgInvite ───────────────────────────────────────────────────────

/**
 * Create an invite for `email` to join `orgId` with `role`.
 *
 * Idempotency: if a non-expired, non-revoked pending invite already exists
 * for the same org+email, returns the existing invite without creating a duplicate.
 *
 * @throws Error if insert fails
 */
export async function createOrgInvite(
  orgId: string,
  inviterUserId: string,
  email: string,
  role: OrgInviteRole,
): Promise<OrgInvite> {
  const normalizedEmail = email.toLowerCase().trim();

  // ── Idempotency check ────────────────────────────────────────────────────
  const { data: existing, error: checkError } = await supabase
    .from('org_invites')
    .select('*')
    .eq('org_id', orgId)
    .eq('email', normalizedEmail)
    .eq('status', 'pending')
    .gt('expires_at', new Date().toISOString())
    .maybeSingle();

  if (checkError) {
    log.warn('[OrgInvite] duplicate check failed', checkError.message);
  }

  if (existing) {
    log.info('[OrgInvite] returning existing pending invite', { orgId, email: normalizedEmail });
    return existing as OrgInvite;
  }

  // ── Insert new invite ────────────────────────────────────────────────────
  const { data, error } = await supabase
    .from('org_invites')
    .insert({
      org_id: orgId,
      inviter_user_id: inviterUserId,
      email: normalizedEmail,
      role,
    })
    .select('*')
    .single();

  if (error || !data) {
    log.error('[OrgInvite] createOrgInvite failed', error?.message);
    throw new Error(`Failed to create invite: ${error?.message ?? 'unknown error'}`);
  }

  log.info('[OrgInvite] invite created', { orgId, email: normalizedEmail, role });
  return data as OrgInvite;
}

// ─── getOrgInviteByToken ──────────────────────────────────────────────────

/**
 * Fetch an invite by its token.
 *
 * Side effect: if the invite is still `pending` but has passed `expires_at`,
 * it is automatically marked `expired` before returning.
 *
 * Returns null if not found.
 */
export async function getOrgInviteByToken(token: string): Promise<OrgInvite | null> {
  const { data, error } = await supabase
    .from('org_invites')
    .select('*')
    .eq('token', token)
    .maybeSingle();

  if (error) {
    log.warn('[OrgInvite] getOrgInviteByToken failed', error.message);
    return null;
  }
  if (!data) return null;

  const invite = data as OrgInvite;

  // Auto-expire if still pending and past expiry
  if (invite.status === 'pending' && new Date(invite.expires_at) < new Date()) {
    const { error: expireError } = await supabase
      .from('org_invites')
      .update({ status: 'expired' })
      .eq('id', invite.id);

    if (expireError) {
      log.warn('[OrgInvite] auto-expire failed', expireError.message);
    }
    return { ...invite, status: 'expired' };
  }

  return invite;
}

// ─── listOrgInvitesForOrg ─────────────────────────────────────────────────

/**
 * List all invites for an org, ordered newest-first.
 *
 * Used by the roster screen's "Pending Invites" section.
 * Callers should filter by status='pending' in the UI if needed.
 */
export async function listOrgInvitesForOrg(orgId: string): Promise<OrgInvite[]> {
  const { data, error } = await supabase
    .from('org_invites')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });

  if (error) {
    log.warn('[OrgInvite] listOrgInvitesForOrg failed', error.message);
    return [];
  }
  return (data as OrgInvite[]) ?? [];
}

// ─── revokeOrgInvite ─────────────────────────────────────────────────────

/**
 * Revoke an invite. Requires `requestingUserId` to be owner/admin of the org.
 *
 * @throws Error if permission check or update fails.
 */
export async function revokeOrgInvite(
  inviteId: string,
  requestingUserId: string,
): Promise<void> {
  // Fetch invite to get org_id for permission check
  const { data: invite, error: fetchError } = await supabase
    .from('org_invites')
    .select('id, org_id, status')
    .eq('id', inviteId)
    .maybeSingle();

  if (fetchError || !invite) {
    throw new Error('Invite not found.');
  }

  // Permission check: must be owner/admin of the org
  const { data: membership, error: memberError } = await supabase
    .from('organization_memberships')
    .select('role')
    .eq('org_id', invite.org_id)
    .eq('user_id', requestingUserId)
    .eq('status', 'active')
    .maybeSingle();

  if (memberError || !membership || !['owner', 'admin'].includes(membership.role)) {
    throw new Error('You do not have permission to revoke this invite.');
  }

  const { error: updateError } = await supabase
    .from('org_invites')
    .update({ status: 'revoked' })
    .eq('id', inviteId);

  if (updateError) {
    log.error('[OrgInvite] revokeOrgInvite failed', updateError.message);
    throw new Error(`Failed to revoke invite: ${updateError.message}`);
  }

  log.info('[OrgInvite] invite revoked', { inviteId });
}

// ─── acceptOrgInvite ─────────────────────────────────────────────────────

/**
 * Accept an org invite.
 *
 * Validates token, checks idempotency (already a member?), creates/upserts
 * membership, and marks invite as accepted.
 *
 * Idempotency:
 * - If user already has active membership → mark invite accepted, return existing role.
 * - Uses upsert on organization_memberships with onConflict: 'org_id,user_id'
 *   so duplicate calls are safe.
 *
 * @returns { orgId, role } on success
 * @throws Error for invalid/expired/revoked tokens
 */
export async function acceptOrgInvite(
  token: string,
  acceptingUserId: string,
): Promise<{ orgId: string; role: string }> {
  // ── Fetch + validate invite ──────────────────────────────────────────────
  const invite = await getOrgInviteByToken(token);

  if (!invite) {
    throw new Error('Invite not found. The link may be invalid.');
  }
  if (invite.status === 'revoked') {
    throw new Error('This invite has been revoked.');
  }
  if (invite.status === 'expired') {
    throw new Error('This invite has expired. Ask the admin to send a new one.');
  }
  if (invite.status === 'accepted') {
    // Already accepted — just return the org/role
    return { orgId: invite.org_id, role: invite.role };
  }
  if (invite.status !== 'pending') {
    throw new Error('This invite is no longer valid.');
  }

  // ── Check existing membership (idempotency) ──────────────────────────────
  const { data: existingMembership } = await supabase
    .from('organization_memberships')
    .select('role, status')
    .eq('org_id', invite.org_id)
    .eq('user_id', acceptingUserId)
    .eq('status', 'active')
    .maybeSingle();

  if (existingMembership) {
    // Already an active member — mark invite accepted (best-effort) and return
    await supabase
      .from('org_invites')
      .update({
        status: 'accepted',
        accepted_at: new Date().toISOString(),
        accepted_by_user_id: acceptingUserId,
      })
      .eq('id', invite.id);

    log.info('[OrgInvite] user already member, marking invite accepted', {
      userId: acceptingUserId.slice(0, 8),
      orgId: invite.org_id,
    });
    return { orgId: invite.org_id, role: existingMembership.role };
  }

  // ── Upsert membership ────────────────────────────────────────────────────
  const { error: membershipError } = await supabase
    .from('organization_memberships')
    .upsert(
      {
        org_id: invite.org_id,
        user_id: acceptingUserId,
        role: invite.role,
        status: 'active',
        is_primary_for_type: false,
      },
      { onConflict: 'org_id,user_id' },
    );

  if (membershipError) {
    log.error('[OrgInvite] membership upsert failed', membershipError.message);
    throw new Error(`Failed to join organization: ${membershipError.message}`);
  }

  // ── Mark invite accepted ─────────────────────────────────────────────────
  const { error: updateError } = await supabase
    .from('org_invites')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString(),
      accepted_by_user_id: acceptingUserId,
    })
    .eq('id', invite.id);

  if (updateError) {
    log.warn('[OrgInvite] failed to mark invite accepted', updateError.message);
    // Non-fatal: membership was created successfully
  }

  log.info('[OrgInvite] invite accepted', {
    userId: acceptingUserId.slice(0, 8),
    orgId: invite.org_id,
    role: invite.role,
  });

  return { orgId: invite.org_id, role: invite.role };
}
